/**
 * PGSS-5384 — shared helpers for the staff list page and table.
 */

import { displayName } from 'app/main/apps/common/AuraFunctions';
import { easternDaysSince } from 'app/main/apps/common/easternTime';

export const DEFAULT_LIST_TYPE = 'customer';

/** Checkbox column. Keep in sync with the first `<th>` in StaffListView. */
export const STAFF_LIST_CHECKBOX_COL_WIDTH = 44;
/** Three icons + More. Tight so leftover card width cannot open a hole after Status. */
export const STAFF_LIST_ACTIONS_COL_WIDTH = 160;
/** Single 32px icon + horizontal padding — floor for adaptive container Actions. */
export const STAFF_LIST_ACTIONS_COL_MIN_WIDTH = 48;
export const STAFF_LIST_ACTIONS_ICON_PX = 32;
export const STAFF_LIST_ACTIONS_PAD_PX = 16;
/** Text columns that absorb leftover width so Status and Actions stay adjacent. */
export const STAFF_LIST_FLEX_COLUMN_KEYS = ['customer', 'customerName', 'ref', 'decedent', 'comments', 'status'];

/**
 * Pinned Actions width from how many icon slots the busiest row needs (1–3 icons, optional More).
 * Container Remaining often has one icon — don't leave it in a 160px gutter.
 */
export function staffListPinnedActionsColWidth(slots) {
  const n = Math.max(1, Math.min(4, Number(slots) || 1));
  return Math.min(
    STAFF_LIST_ACTIONS_COL_WIDTH,
    Math.max(STAFF_LIST_ACTIONS_COL_MIN_WIDTH, n * STAFF_LIST_ACTIONS_ICON_PX + STAFF_LIST_ACTIONS_PAD_PX),
  );
}

export function staffListTableMinWidth(visibleCols = [], { includeActions = true, actionsWidth } = {}) {
  const actions = includeActions
    ? (Number(actionsWidth) > 0 ? Number(actionsWidth) : STAFF_LIST_ACTIONS_COL_WIDTH)
    : 0;
  return STAFF_LIST_CHECKBOX_COL_WIDTH
    + actions
    + visibleCols.reduce((sum, col) => sum + (Number(col.width) || 0), 0);
}

/** PGSS-5759: tooltip and accessible name of the icon on rows with internal notes. */
export const STAFF_LIST_INTERNAL_NOTES_LABEL = 'Has internal notes';

/**
 * PGSS-5759: the RFQ, Quote and Order lists flag a row whose Internal Notes has something this user can open. The
 * API sends has_internal_notes on staff lists only, worked out with the same visibility as the dialog.
 */
export function staffListShowsInternalNotesIcon(listType, original) {
  return ['rfq', 'quote', 'order'].includes(listType) && original?.has_internal_notes === true;
}

/** Same set the old staff Orders list keeps when Type is Supplier Orders (any supplier). */
export const STAFF_LIST_SUPPLIER_ROW_ACTIONS = ['View Order', 'Receive Order'];

/**
 * Types whose rows carry no actions.
 *
 * PGSS-5409 (QA): the old Quotes / RFQs page drops the whole action set on the archived
 * lists — `OrdersRowActions` gates them on `&archive=true` being in the address bar, and
 * the old Type filter writes `?cid=all&sales_person=all&archive=true` there. This page
 * writes `archive` first, so that substring check never matches and archived quotes and
 * RFQs kept their actions. Named here instead so the rule does not depend on parameter
 * order. Orders is deliberately absent: `customer_archive` writes `?archive=true&...`,
 * so the old staff Orders list shows actions on archived orders and must keep doing so.
 */
export const STAFF_LIST_NO_ROW_ACTION_TYPES = ['back_order', 'archive_quote', 'archive_rfq'];

export function hideStaffListRowActions(typeValue) {
  return STAFF_LIST_NO_ROW_ACTION_TYPES.includes(typeValue);
}

export function isStaffListSupplierType(typeValue) {
  return typeValue === 'supplier' || typeValue === 'supplier_archive';
}

function asSearchParams(search) {
  return search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
}

/** Same All-supplier object applyListType writes when Type is Supplier Orders. */
export const STAFF_LIST_ALL_PARTY = { name: 'All', value: 'all', email: '', id: 'all' };

export function staffListIsContainer(listType) {
  return listType === 'container';
}

/** Legacy OrdersTabView tabs. Order is the order they render in. */
export const STAFF_LIST_CONTAINER_TABS = [
  { viewType: 'container-awaiting-approval', label: 'Awaiting Approval' },
  { viewType: 'container-received', label: 'Container to be Received' },
  { viewType: 'in-loading', label: 'In-Loading' },
  { viewType: 'remaining-shipments', label: 'Remaining shipments' },
];

export const STAFF_LIST_CONTAINER_DEFAULT_TAB = STAFF_LIST_CONTAINER_TABS[0].viewType;

/** Columns the container list renders, in display order. Everything else is hidden for it. */
export const STAFF_LIST_CONTAINER_COLUMN_KEYS = [
  'container',
  'ctn',
  'customer',
  'shipmentDate',
  'eta',
  'weight',
  'capacity',
  'noOfOrders',
  'type',
  'location',
  'status',
];

/** Container-only columns: absent from the picker (and the table) on every other list. */
export const STAFF_LIST_CONTAINER_ONLY_COLUMN_KEYS = ['shipmentDate', 'capacity', 'noOfOrders', 'location'];

/**
 * The tab in the URL; when the URL has none (or a stale one), `fallback` if it is a real tab
 * (PGSS-5670: the remembered tab), else the first tab.
 */
export function staffListContainerTabFromUrl(search, fallback) {
  const isTab = (value) => STAFF_LIST_CONTAINER_TABS.some((tab) => tab.viewType === value);
  const value = (asSearchParams(search).get('viewType') || '').trim();
  if (isTab(value)) return value;
  return isTab(fallback) ? fallback : STAFF_LIST_CONTAINER_DEFAULT_TAB;
}

/**
 * Switching tab keeps only the new viewType — filters, sort and paging are cleared, as
 * OrdersTabView did. The default tab is still written so the request and the URL agree.
 */
export function staffListContainerTabSearch(viewType) {
  const sp = new URLSearchParams();
  sp.set('viewType', viewType || STAFF_LIST_CONTAINER_DEFAULT_TAB);
  return `?${sp.toString()}`;
}

/** True when the URL has no valid viewType (arrival on the bare route, or a stale value). */
export function staffListContainerUrlMissingTab(search) {
  return asSearchParams(search).get('viewType') !== staffListContainerTabFromUrl(search);
}

/** Current search with the tab written in, for arriving on the list without one. */
export function staffListContainerWithTab(search, viewType) {
  const sp = new URLSearchParams(search || '');
  sp.set('viewType', viewType || STAFF_LIST_CONTAINER_DEFAULT_TAB);
  return `?${sp.toString()}`;
}

export function staffListContainerStatusCategory(viewType) {
  return `${viewType || STAFF_LIST_CONTAINER_DEFAULT_TAB}_status`;
}

export function staffListRecordNoLabel(listType) {
  if (listType === 'quote') return 'Quote no';
  if (listType === 'rfq') return 'RFQ no';
  if (listType === 'invoice') return 'Invoice no';
  return 'Order no';
}

/** Same wording as the current Quotes / RFQs / Orders tables. */
export function staffListEtaLabel(listType) {
  if (listType === 'rfq') return 'Quote ETA';
  if (listType === 'container') return 'ETA Date';
  return 'Delivery ETA';
}

/** Old list header is `${capitalize(type)}  Date` — Quote Date / Rfq Date / Order Date. */
export function staffListDateLabel(listType) {
  if (listType === 'quote') return 'Quote date';
  if (listType === 'rfq') return 'RFQ date';
  if (listType === 'invoice') return 'Invoice date';
  return 'Order date';
}

export function staffListShowsContainer(listType) {
  return listType === 'order' || listType === 'invoice';
}

/** Same audience as the old Amount column, which is hidden for RFQs. */
export function staffListShowsAmount(listType) {
  return listType !== 'rfq' && listType !== 'container';
}

/** Invoice due amount — customer and supplier invoice lists only. */
export function staffListShowsDue(listType) {
  return listType === 'invoice';
}

/** Paid amount on invoice lists (same as the old Paid / Invoice Paid column). */
export function staffListShowsPaid(listType) {
  return listType === 'invoice';
}

/**
 * PGSS-4325: the customer's payment type (e.g. COD, Net 30) on customer invoices. The legacy invoice
 * table showed it to staff and hid it on supplier invoices; the redesigned list dropped it.
 */
export function staffListShowsPaymentType(listType, isSupplierInvoice = false) {
  return listType === 'invoice' && !isSupplierInvoice;
}

/**
 * PGSS-5636: the old invoice list's Due date. Optional — off until picked in Visible columns.
 * Shown for both customer and supplier invoices; the old column had no hide rule.
 */
export function staffListShowsDueDate(listType) {
  return listType === 'invoice';
}

/** PGSS-5636: Shipped date. The old list hid it on supplier invoices and for supplier login. */
export function staffListShowsShippedDate(listType, isSupplierInvoice = false) {
  return listType === 'invoice' && !isSupplierInvoice;
}

/**
 * PGSS-5636: CTN (container tracking number). The old list showed it on supplier invoices
 * and hid it for staff on customer invoices. Distinct from Container no.
 */
export function staffListShowsCtn(listType, isSupplierInvoice = false) {
  return listType === 'invoice' && Boolean(isSupplierInvoice);
}

/** Tax / tariffs & customs fee — invoice lists only (matches invoice detail header). */
export function staffListShowsTax(listType) {
  return listType === 'invoice';
}

/**
 * Customer invoices use "Applied Amount" (displayName.invoicePaidAmt); supplier invoices use "Paid".
 * Matches getPaidAmtLabel() on the legacy invoice table and OrderHeader.
 */
export function staffListPaidLabel(listType, isSupplierInvoice = false) {
  if (listType !== 'invoice') return 'Paid';
  if (isSupplierInvoice) return 'Paid';
  return displayName?.invoicePaidAmt || 'Applied Amount';
}

export function staffListTaxLabel() {
  return displayName?.tax || 'Tariffs & Customs Fee';
}

/** Same audience as the old Weight (lbs) column, which is on Quotes only. */
export function staffListShowsWeight(listType) {
  return listType === 'quote' || listType === 'container';
}

/** PGSS-5713: how long an order has sat in its status. Order-shaped lists only; an invoice has no status change time. */
export function staffListShowsStatusDays(listType) {
  return listType !== 'invoice' && listType !== 'container';
}

/**
 * Whole Eastern calendar days since the order last changed status, or null when the time is unknown: changed today
 * (Eastern) is 0, late yesterday is 1. The server sends this as days_in_status; this is the fallback when it doesn't.
 * Accepts an ISO string, epoch seconds or milliseconds.
 */
export function staffListDaysInStatus(statusChangeTime, now = Date.now()) {
  if (!statusChangeTime) return null;
  return easternDaysSince(statusChangeTime, new Date(now));
}

/** Same audience as the old Quote Sent/Received column, which is on RFQs only. */
export function staffListShowsQuoteCounts(listType) {
  return listType === 'rfq';
}

/** Order-shaped columns that invoices do not carry. */
export function staffListShowsOrderOnlyColumn(listType, key) {
  if (listType === 'container') return STAFF_LIST_CONTAINER_COLUMN_KEYS.includes(key);
  if (listType !== 'invoice') return true;
  return !['decedent', 'comments', 'eta', 'qty', 'type', 'ref'].includes(key);
}

/**
 * Quote Sent/Received, as the old RFQ column renders it: `sent / replied`, plus a
 * red `(n Rejected)` when any were rejected. Null when nothing has been sent —
 * the old cell falls back to an empty string on a falsy count, so a 0 stays blank
 * rather than reading `0 / 0`. Neither sortable nor filterable there, so no sort
 * or param key goes with it.
 */
export function staffListQuoteCounts(order) {
  if (!order?.sent_to_supplier_count) return null;
  const rejected = order.rejected_quote_count;
  return {
    sentReplied: `${order.sent_to_supplier_count} / ${order.replied_quote_count}`,
    rejected: rejected && Number(rejected) > 0 ? `(${rejected} Rejected)` : '',
  };
}

/**
 * PGSS-5409 (QA): the old page's header links are not one import/export gate — see
 * `handleShow` in Orders.js, which reads the list name. Import Multiple and Import
 * Archive are Orders and Quotes; Import Single also covers RFQs; Export is Orders and
 * Quotes. QA raised the missing Import Single RFQ and the Export that RFQs do not have.
 */
export function staffListShowsMultipleImport(listType) {
  return ['order', 'quote'].includes(listType);
}

export function staffListShowsSingleImport(listType) {
  return ['order', 'quote', 'rfq'].includes(listType);
}

export function staffListShowsExport(listType) {
  return ['order', 'quote', 'invoice'].includes(listType);
}

/**
 * Type option from the URL, by value — not by a frozen options[n] index.
 * Order, Quote, and RFQ each have their own flags. Mixing them would snap
 * Archived Quote/RFQ back to Active (or onto Supplier Orders). Callers must
 * skip Type when this returns null.
 */
export function staffListTypeFromSearch(search, options = [], listType = 'order') {
  const sp = asSearchParams(search);
  const byValue = (value) => options.find((option) => option.value === value) || null;
  if (listType === 'quote') {
    if (sp.has('archive')) return byValue('archive_quote') || options[0] || null;
    return byValue('active_quote') || options[0] || null;
  }
  if (listType === 'rfq') {
    if (sp.has('archive')) return byValue('archive_rfq') || options[0] || null;
    return byValue('active_rfq') || options[0] || null;
  }
  if (listType === 'invoice') {
    // Customer vs supplier Type options come from getFilterOptions(..., isSupplierInvoice).
    if (sp.has('sid') && sp.has('archive')) return byValue('supplier_archive') || options[0] || null;
    if (sp.has('sid')) return byValue('supplier') || options[0] || null;
    if (sp.has('archive')) {
      return byValue('customer_archive') || byValue('supplier_archive') || options[0] || null;
    }
    return byValue('customer') || byValue('supplier') || options[0] || null;
  }
  if (listType !== 'order') return null;
  if (sp.has('reorder')) return byValue('reorder') || options[0] || null;
  if (sp.has('back_order')) return byValue('back_order') || options[0] || null;
  if (sp.has('sid') && sp.has('archive')) return byValue('supplier_archive') || options[0] || null;
  if (sp.has('archive')) return byValue('customer_archive') || options[0] || null;
  if (sp.has('sid')) return byValue('supplier') || options[0] || null;
  return byValue('customer') || options[0] || null;
}

/** Missing flag is off. Do not leave a previous chip on because the param is absent. */
export function staffListScopeFromSearch(search) {
  const sp = asSearchParams(search);
  return {
    invoiced: sp.get('include_invoice_status') === 'true',
    archived: sp.get('include_archive') === 'true',
    vas: sp.get('vas_order') === 'true',
    actionable: sp.get('actionableOnly') === 'true',
    cancelled: sp.get('include_cancelled') === 'true',
  };
}

export function staffListPartyFromLists(id, list) {
  if (id == null || id === '' || id === 'all') return null;
  return (list || []).find((item) => String(item?.id) === String(id)) || null;
}

export function staffListSameParty(left, right) {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return String(left.id) === String(right.id);
}

/**
 * Customer / supplier / sales person to show from the URL.
 * Each `*Pending` flag is independent so a missing sales-person row cannot
 * block restoring the customer (and the other way around).
 */
export function staffListPartiesFromSearch(search, {
  customers = [],
  suppliers = [],
  salesPersons = [],
  typeValue,
} = {}) {
  const sp = asSearchParams(search);
  if (isStaffListSupplierType(typeValue)) {
    const sid = sp.get('sid');
    const found = staffListPartyFromLists(sid, suppliers);
    return {
      customer: null,
      supplier: (!sid || sid === 'all') ? STAFF_LIST_ALL_PARTY : found,
      salesPerson: null,
      customerPending: false,
      supplierPending: Boolean(sid && sid !== 'all' && !found),
      salesPending: false,
    };
  }
  const cid = sp.get('cid');
  const salesId = sp.get('sales_person');
  const customer = staffListPartyFromLists(cid, customers);
  const salesPerson = staffListPartyFromLists(salesId, salesPersons);
  return {
    customer,
    supplier: null,
    salesPerson,
    customerPending: Boolean(cid && cid !== 'all' && !customer),
    supplierPending: false,
    salesPending: Boolean(salesId && salesId !== 'all' && !salesPerson),
  };
}

export function isStaffListSupplierRowAction(label) {
  const text = String(label || '').trim().toLowerCase();
  return STAFF_LIST_SUPPLIER_ROW_ACTIONS.some((name) => name.toLowerCase() === text);
}

/** Which row-action labels this Type is allowed to show.
 *  Supplier Orders whitelist must NOT apply to invoices: supplier invoice Type values
 *  reuse `supplier` / `supplier_archive`, but their actions are View Invoice etc.
 */
export function staffListRowActionAllowed(typeValue, label, listType = 'order') {
  if (hideStaffListRowActions(typeValue)) return false;
  if (listType === 'invoice') return true;
  if (isStaffListSupplierType(typeValue)) return isStaffListSupplierRowAction(label);
  return true;
}

/** Invoice numbers are longer than order nos; give them room before flex columns share leftover. */
export const STAFF_LIST_CONTAINER_COLUMN_WIDTHS = {
  // Number plus the edit pencil; longer numbers wrap (PGSS-5655).
  container: 168,
  ctn: 100,
  customer: 190,
  shipmentDate: 120,
  // "Sep 30, 2026" needs ~112px; narrower wraps under fixed table layout.
  eta: 120,
  weight: 100,
  capacity: 190,
  noOfOrders: 100,
  type: 100,
  location: 100,
  status: 220,
};

export function staffListResolvedColumn(col, listType = 'order') {
  if (!col) return col;
  if (listType === 'container' && STAFF_LIST_CONTAINER_COLUMN_WIDTHS[col.key]) {
    return { ...col, width: STAFF_LIST_CONTAINER_COLUMN_WIDTHS[col.key] };
  }
  if (listType === 'invoice' && col.key === 'orderNo') {
    return { ...col, width: 168 };
  }
  if (listType === 'invoice' && (col.key === 'customer' || col.key === 'customerName')) {
    return { ...col, width: 148 };
  }
  if (listType === 'invoice' && col.key === 'status') {
    return { ...col, width: 168 };
  }
  return col;
}

/**
 * PGSS-5556: headers whose label comes from a configurable display name (Tariffs & Customs Fee,
 * Applied Amount) are longer than their column. Under tableLayout:'fixed' a nowrap header cannot
 * widen its column, so it overlapped the next one. Let these wrap onto a second line instead —
 * widening would only hold until someone configures a longer label.
 */
export const STAFF_LIST_WRAPPING_HEADER = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  lineHeight: 1.2,
};

export function staffListColumnWidthStyle(col) {
  if (!col) return {};
  if (STAFF_LIST_FLEX_COLUMN_KEYS.includes(col.key)) {
    return { minWidth: col.width };
  }
  return { width: col.width, minWidth: col.width };
}

/** Lists whose checkbox, Order no and Actions columns stay in view while the table scrolls sideways. */
export const STAFF_LIST_PINNED_LIST_TYPES = ['order', 'quote', 'rfq'];

/**
 * Back Order and archived Quotes / RFQs are views of those lists (same listType, their own
 * typeValue) with no Actions column (STAFF_LIST_NO_ROW_ACTION_TYPES), so they stay unpinned.
 */
export function staffListPinsColumns(listType, typeValue) {
  // Container shipments pin their Container Number the way Orders pins Order no.
  if (listType === 'container') return true;
  return STAFF_LIST_PINNED_LIST_TYPES.includes(listType) && !hideStaffListRowActions(typeValue);
}

/**
 * Pinned Order no width, padding included (the pinned cells are border-box in staffList.css).
 *
 * Measured in Chromium with real rows rendered through OrderViewAction, the app's own icon font
 * (Material Icons Outlined, so the VAS brush draws as a 14px glyph) and this app's Tailwind scale
 * (px-2 / ml-2 are 0.2rem = 2px; the root font size is 62.5%). The cell keeps 18px of padding
 * (4px left, 14px right) and the Typography's px-2 is dropped in staffList.css. At 92px:
 *   5-digit number (today's orders, e.g. 24168) with the VAS icon: 19px to spare
 *   6-digit with the VAS icon: 11px; 7-digit with the VAS icon: 3px
 * and the "ORDER NO" header with its sort arrow fits. The unpinned column is 112px plus 18px
 * padding, drawn 130px wide.
 */
export const STAFF_LIST_PINNED_ORDER_NO_WIDTH = 92;

/** Column as drawn when the grid is pinned: only Order no changes, and only when pinned. */
export function staffListPinnedColumn(col, pinned) {
  if (!pinned || col?.key !== 'orderNo') return col;
  return { ...col, width: STAFF_LIST_PINNED_ORDER_NO_WIDTH };
}

/** Cell classes for the pinned columns (staffList.css, under .staff-list-grid--pinned). */
export const STAFF_LIST_PIN_CLASS = {
  check: 'staff-list-pin-check',
  orderNo: 'staff-list-pin-no',
  actions: 'staff-list-pin-actions',
};

export function staffListActionsColStyle(width = STAFF_LIST_ACTIONS_COL_WIDTH) {
  const w = Number(width) > 0 ? Number(width) : STAFF_LIST_ACTIONS_COL_WIDTH;
  return {
    width: w,
    minWidth: w,
    maxWidth: w,
  };
}

/** Same wording as today's Orders list (Include Invoiced Order / Include Archive Order / Only VAS). */
export const STAFF_LIST_SCOPE_LABELS = {
  invoiced: 'Include Invoiced Order',
  archived: 'Include Archive Order',
  vas: 'Only VAS',
  actionable: 'Actionable only',
  cancelled: 'Include Cancelled Invoices',
};

/** Same wording as the legacy container shipments filter. */
export function staffListScopeLabel(key, listType) {
  if (listType === 'container' && key === 'archived') return 'Include Archive Container';
  return STAFF_LIST_SCOPE_LABELS[key];
}

/** Default hidden columns. Invoice order-only keys stay false in cols so a stale
 *  layout (or ungated render) cannot resurface Reference/Notes/ETA on invoices. */
export function staffListDefaultHiddenColumns(listType) {
  if (listType === 'container') return [];
  if (listType === 'invoice') {
    // dueDate / shippedDate / CTN are optional selections: available in the picker, off until chosen.
    return ['comments', 'type', 'decedent', 'eta', 'qty', 'ref', 'weight', 'quoteCounts', 'dueDate', 'shippedDate', 'ctn'];
  }
  return ['comments', 'type', 'container', 'decedent', 'statusDays'];
}

/** Legacy unscoped key from the first persist. Read only when no user id is passed. */
export const STAFF_LIST_LAYOUT_KEY = 'staff-list-orders-layout';

export function staffListLayoutUserId(user) {
  return user?.uuid || user?.id || '';
}

export function staffListLayoutKey(listType = 'order', userId) {
  const base = `staff-list-${listType || 'order'}-layout`;
  return userId ? `${base}-${userId}` : base;
}

function parseStaffListLayout(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function staffListDefaultCols(columnKeys = [], hiddenColumns = []) {
  return columnKeys.reduce((acc, key) => {
    acc[key] = !hiddenColumns.includes(key) && key !== 'customerName';
    return acc;
  }, {});
}

export function readStaffListLayout(storage, listType = 'order', userId) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    if (userId) {
      return parseStaffListLayout(store?.getItem?.(staffListLayoutKey(listType, userId)));
    }
    let raw = store?.getItem?.(staffListLayoutKey(listType));
    if (!raw && (listType === 'order' || !listType)) {
      raw = store?.getItem?.(STAFF_LIST_LAYOUT_KEY);
    }
    return parseStaffListLayout(raw);
  } catch {
    return null;
  }
}

export function writeStaffListLayout(layout, storage, listType = 'order', userId) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    store?.setItem?.(staffListLayoutKey(listType, userId), JSON.stringify(layout));
  } catch {
    // private mode / quota
  }
}

/** Header Filters badge stays Type/people/sales/scope only. Reset clears those plus column filters. */
export function staffListResetClearsCount(filterCount = 0, colFilterCount = 0) {
  return Number(filterCount) + Number(colFilterCount);
}

export function mergeStaffListCols(defaults = {}, storedCols) {
  if (!storedCols || typeof storedCols !== 'object') return { ...defaults };
  const next = { ...defaults };
  Object.keys(defaults).forEach((key) => {
    if (typeof storedCols[key] === 'boolean') next[key] = storedCols[key];
  });
  return next;
}

/** Old Orders always shows the column-filter row; default open unless the user hid it. */
export function staffListColFiltersOpen(stored, fallback = true) {
  if (stored && typeof stored.colFiltersOpen === 'boolean') return stored.colFiltersOpen;
  return fallback;
}

export function mapStaffListRow(order, formatDate, pickAmount, listType = 'order') {
  if (!order) return null;
  if (listType === 'container') {
    const shipmentId = order.shipment_id || order.id;
    const date = (value) => (formatDate && value && formatDate(value)) || '—';
    return {
      id: String(shipmentId),
      recordId: shipmentId,
      container: order.container_number || '—',
      ctn: order.container_tracking_number || '—',
      customer: order.name || order.customer_name || '—',
      customerName: order.customer_name || '—',
      shipmentDate: date(order.shipment_date),
      eta: date(order.eta_date),
      weight: order.weight,
      noOfOrders: order.no_of_orders ?? '—',
      type: order.order_type || '—',
      location: order.location || '—',
      status: order.status?.name || '',
      orderDate: '—',
      ref: '—',
      decedent: '—',
      notes: '',
      qty: 0,
      quoteCounts: null,
      amount: 0,
      tax: null,
      paid: null,
      due: null,
      paymentType: null,
      dueDate: null,
      shippedDate: null,
      original: order,
    };
  }
  // Prefer listType over shape sniffing — OrderResponse must never quietly map as an invoice
  // if it later gains invoice_no / invoice_amount fields.
  if (listType === 'invoice') {
    const partyName =
      order.supplier?.name || order.company_name || order.customer_name || order.name || '—';
    return {
      id: String(order.invoice_no || order.id),
      recordId: order.id,
      customer: partyName,
      customerName: order.customer_name || '—',
      orderDate: (formatDate && order.invoice_date && formatDate(order.invoice_date)) || '—',
      ref: order.order_no || order.customer_provider_number || '—',
      decedent: '—',
      notes: '',
      eta: '—',
      qty: 0,
      weight: null,
      quoteCounts: null,
      amount: Number(order.invoice_amount || 0),
      tax: Number(order.tax || 0),
      paid: Number(order.paid_amount || 0),
      due: Number(order.due_amount || 0),
      paymentType: order.customer_payment_type || '—',
      dueDate: (formatDate && order.due_date && formatDate(order.due_date)) || '—',
      shippedDate: (formatDate && order.shipped_date && formatDate(order.shipped_date)) || '—',
      ctn: order.container_tracking_number || '—',
      type: order.order_type || '—',
      container: order.container_number || '—',
      status: order.status?.name || order.status?.display_name || '',
      original: order,
    };
  }
  return {
    id: String(order.order_no || order.id),
    recordId: order.id,
    customer: order.company_name || order.customer_name || order.name || '—',
    customerName: order.customer_name || '—',
    orderDate: (formatDate && order.date && formatDate(order.date)) || '—',
    ref: order.customer_provider_number || order.reference_no || '—',
    decedent: order.decedents || '—',
    notes: order.customer_notes || '',
    eta: (formatDate && order.eta_date && formatDate(order.eta_date)) || '—',
    qty: Number(order.quantity || 0),
    weight: order.weight,
    quoteCounts: staffListQuoteCounts(order),
    amount: Number((pickAmount && pickAmount(order.all_invoices_pre_tax_total, order.displayAmount)) || order.displayAmount || 0),
    tax: null,
    paid: null,
    due: null,
    paymentType: null,
    dueDate: null,
    shippedDate: null,
    ctn: null,
    type: order.order_type || 'R',
    container: order.container_number || '—',
    status: order.status?.name || '',
    // PGSS-5713: the server's Eastern calendar-day count; the browser's own count only if it is missing.
    statusDays: typeof order.days_in_status === 'number' ? order.days_in_status : staffListDaysInStatus(order.status_change_time),
    original: order,
  };
}

const HEADER_ALIASES = {
  'Customer Name': 'Customer',
  'Supplier Name': 'Supplier',
  Comments: 'Notes',
  'Order Type': 'Type',
  'Container No': 'Container',
  'Container Number': 'Container',
};

export function countStaffListFilters({
  filterType,
  customer,
  salesPerson,
  includeInvoice,
  includeArchive,
  showOnlyVAS,
  vasTypes,
  actionableOnly,
  includeCancelled,
} = {}) {
  let count = 0;
  const defaultTypes = ['customer', 'active_rfq', 'active_quote'];
  if (filterType?.value && !defaultTypes.includes(filterType.value)) {
    count += 1;
  }
  if (customer?.id && customer.id !== 'all') count += 1;
  if (salesPerson?.id && salesPerson.id !== 'all') count += 1;
  if (includeInvoice) count += 1;
  if (includeArchive) count += 1;
  if (showOnlyVAS) count += 1;
  if (showOnlyVAS && vasTypes?.length) count += 1;
  if (actionableOnly) count += 1;
  if (includeCancelled) count += 1;
  return count;
}

export function staffStatusTone(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('revision') || n.includes('hold') || n.includes('cancel') || n.includes('reject')) {
    return { bg: '#fff3e6', fg: '#9a4b06', border: '#fde0c0', dot: '#ef8b1c' };
  }
  if (n.includes('announc')) {
    return { bg: '#eaf0ff', fg: '#1a44a8', border: '#d3e0ff', dot: '#2f6bf0' };
  }
  if (n.includes('ready') || n.includes('ship') || n.includes('complete') || n.includes('deliver')) {
    return { bg: '#e7f5ec', fg: '#0f6b3d', border: '#c6e8d3', dot: '#1a9457' };
  }
  return { bg: '#f1f3f6', fg: '#5f6b7a', border: '#e3e8ef', dot: '#8c97a6' };
}

function NotesIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function statusPill(status, fallback) {
  if (!status) {
    return fallback;
  }
  const tone = staffStatusTone(status.name);
  return (
    <span
      className="staff-list-status"
      style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
      title={status.name}
    >
      <span className="staff-list-status-dot" style={{ background: tone.dot }} />
      {status.name}
    </span>
  );
}

export function withStaffStatusPills(columns) {
  return (columns || []).map((col) => {
    if (col.Header !== 'Status' || col.accessor !== 'status.name') {
      return col;
    }
    const OriginalCell = col.Cell;
    return {
      ...col,
      sxStyle: { width: 220, minWidth: 200, maxWidth: 280 },
      headerSxStyle: { width: 220, minWidth: 200, maxWidth: 280 },
      Cell: (cellProps) => {
        const status = cellProps.row?.original?.status;
        if (!status) {
          return OriginalCell ? OriginalCell(cellProps) : null;
        }
        return statusPill(status);
      },
    };
  });
}

/**
 * Restyle existing AuraTable columns to the staff-list mockup without
 * changing shared column definitions.
 */
export function withStaffListColumns(columns) {
  return withStaffStatusPills(columns || []).map((col) => {
    const next = { ...col };

    if (typeof col.Header === 'string' && HEADER_ALIASES[col.Header]) {
      next.Header = HEADER_ALIASES[col.Header];
    }

    if (col.id === 'selection') {
      next.hideForType = false;
    }

    if (col.id === 'action') {
      next.Header = 'Actions';
      next.sxStyle = { width: 178, minWidth: 160, maxWidth: 220 };
      next.headerSxStyle = { width: 178, minWidth: 160, maxWidth: 220 };
    }

    if (col.accessor === 'order_no') {
      next.sxStyle = { width: 112, minWidth: 112, maxWidth: 140 };
      next.headerSxStyle = { width: 112, minWidth: 112, maxWidth: 140 };
    }

    if (col.accessor === 'order_type') {
      const OriginalCell = col.Cell;
      next.Cell = (cellProps) => {
        const value = cellProps.value ?? cellProps.row?.original?.order_type;
        if (!value) {
          return OriginalCell ? OriginalCell(cellProps) : null;
        }
        return (
          <span className="staff-list-type-badge" title="Regular order">
            {value}
          </span>
        );
      };
    }

    if (col.Header === 'Comments' || next.Header === 'Notes') {
      if (col.accessor === 'customer_notes') {
        next.sxStyle = { width: 76, maxWidth: 76 };
        next.headerSxStyle = { width: 76, maxWidth: 76 };
        next.Cell = (cellProps) => {
          const notes = cellProps.row?.original?.customer_notes;
          return (
            <span className={`staff-list-notes${notes ? ' has-notes' : ''}`} title={notes || 'Notes'}>
              <NotesIcon />
            </span>
          );
        };
      }
    }

    return next;
  });
}

/**
 * PGSS-5753: RFQ, Quote and Order lists page by 25 / 50 / 75 (default 25), and remember the
 * choice per list in this browser. Invoices and Container Shipments keep 5 / 10 / 25 (default 10).
 *
 * The chosen size is always written into the URL (`limit`) for these three lists, because the
 * toolbar and row actions refetch from the URL alone; a size held only in state would make them
 * fall back to the API's 10.
 */
const STAFF_LIST_REMEMBERED_PAGE_SIZES = {
  order: [25, 50, 75],
  quote: [25, 50, 75],
  rfq: [25, 50, 75],
};

const STAFF_LIST_DEFAULT_PAGE_SIZES = [5, 10, 25];

export function staffListRemembersPageSize(listType) {
  return Boolean(STAFF_LIST_REMEMBERED_PAGE_SIZES[listType]);
}

export function staffListPageSizeOptions(listType) {
  return STAFF_LIST_REMEMBERED_PAGE_SIZES[listType] || STAFF_LIST_DEFAULT_PAGE_SIZES;
}

export function staffListDefaultPageSize(listType) {
  return staffListRemembersPageSize(listType) ? staffListPageSizeOptions(listType)[0] : 10;
}

export function staffListPageSizeKey(listType) {
  return `staff-list-${listType || 'order'}-page-size`;
}

/** The remembered size for this list, or its default. A stored size no longer offered is ignored. */
export function readStaffListPageSize(listType, storage) {
  const fallback = staffListDefaultPageSize(listType);
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    const stored = Number(store?.getItem?.(staffListPageSizeKey(listType)));
    return staffListPageSizeOptions(listType).includes(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

export function writeStaffListPageSize(listType, size, storage) {
  if (!staffListRemembersPageSize(listType)) return;
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    store?.setItem?.(staffListPageSizeKey(listType), String(size));
  } catch {
    // private mode / quota: the list still opens on its default
  }
}

/** True when an RFQ / Quote / Order list URL has no `limit` yet. */
export function staffListUrlMissingPageSize(search, listType) {
  return staffListRemembersPageSize(listType) && !asSearchParams(search).get('limit');
}

/** Current search with the page size written in. */
export function staffListWithPageSize(search, size) {
  const sp = new URLSearchParams(search || '');
  sp.set('limit', size);
  return `?${sp.toString()}`;
}
